import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

// Components
import { PlayerListContainer } from './player-list.container';

// Data
import { getAuthUser } from '../../../lib/services/auth.service';
import { PLAYER_FIELDS } from '../../../lib/queries/player.queries';
import { POSITION_FIELDS } from '../../../lib/queries/position.queries';
import { AGE_GROUP_FIELDS } from '../../../lib/queries/age-group.queries';
import { REPORT_CARD_ACCESS_FIELDS } from '../../../lib/queries/report-card-access.queries';
import {buildRemovePlayerAssessments} from "../../../lib/services/player-assessment.service";
import { getAgeGroups } from '../../../lib/services/age-group.service';
import { useLastValueWhere } from '../../../lib/hooks/use-last-value-where';
import { ROLES } from '../../../../_configuration';

export const GET_PLAYER_LIST = gql`
  query GetPlayerList($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      _id
      player {
        ...PlayerFields
      }
      position {
        ...PositionFields
      }
      ageGroup {
        ...AgeGroupFields
      }
      assessmentSessionCount
      isHeld
      isConcluded
      unappliedActionsCount
      ...ReportCardAccessFields
      contactReach
    }
  }

  ${PLAYER_FIELDS}
  ${POSITION_FIELDS}
  ${AGE_GROUP_FIELDS}
  ${REPORT_CARD_ACCESS_FIELDS}
`;

export const PlayerListData = () => {
  const user = getAuthUser();
  const { removePlayerAssessments } = buildRemovePlayerAssessments();
  const { ageGroups: allowedEditingAgeGroups, loading: ageGroupsLoading } = getAgeGroups({requiredRoles: [ROLES.CONFIGURATION], useActive: true})
  const { data, previousData, loading, error } = useQuery(GET_PLAYER_LIST, {
    skip: !user,
    variables: { assessmentId: user?.settings.activeAssessmentId, ageGroupIds: allowedEditingAgeGroups.map(a => a._id) },
    fetchPolicy: 'cache-and-network'
  });

  // Not memoized: a property read off an Apollo result, so the array identity is already stable.
  const fetchedRows: IPlayerAssessment[] | undefined = (data || previousData)?.playerAssessments;

  // An import re-keys this query as its age-group scope settles, and every intermediate key
  // answers empty — so an empty list only reaches the table once neither query is in flight.
  const rows = useLastValueWhere(
    fetchedRows,
    candidate => Boolean(candidate?.length) || !(loading || ageGroupsLoading),
    undefined
  );

  const playerAssessments = useMemo(
    () => (rows || []).map(p => ({...p, editingDisabled: !allowedEditingAgeGroups.find(a => a._id === p.ageGroup._id) })),
    [allowedEditingAgeGroups, rows]
  )

  return <PlayerListContainer
    removePlayerAssessments={removePlayerAssessments}
    playerAssessments={playerAssessments}
    loading={loading}
    fetchError={Boolean(error)}
  />;
};
